import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StepIndicator } from '../components/ui/StepIndicator';
import { ArrowRightIcon, ArrowLeftIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const STEPS = [{
  id: 'type',
  label: 'What are you building?'
}, {
  id: 'charge',
  label: 'How do you charge?'
}, {
  id: 'products',
  label: 'Products & Pricing'
}, {
  id: 'features',
  label: 'Features & Invoices'
}, {
  id: 'billing',
  label: 'Billing Management'
}, {
  id: 'stack',
  label: 'Tech Stack'
}, {
  id: 'stripe',
  label: 'Stripe Account'
}, {
  id: 'review',
  label: 'Review'
}];
interface WizardState {
  buildingType: string;
  chargeModel: string;
  features: {
    trials: boolean;
    coupons: boolean;
    tax: boolean;
    invoices: boolean;
  };
  billingManager: string;
  stack: {
    frontend: string;
    backend: string;
    database: string;
  };
  stripeAccount: string;
  stripeAccountId: string;
  products: {
    name: string;
    price: number;
    currency: string;
    billingType: 'recurring_monthly' | 'recurring_annual' | 'recurring_both' | 'one_time';
  }[];
}
const INITIAL_STATE: WizardState = {
  buildingType: '',
  chargeModel: '',
  features: {
    trials: false,
    coupons: false,
    tax: false,
    invoices: false
  },
  billingManager: '',
  stack: {
    frontend: '',
    backend: '',
    database: ''
  },
  stripeAccount: '',
  stripeAccountId: '',
  products: [{
    name: 'Starter',
    price: 9,
    currency: 'USD',
    billingType: 'recurring_both'
  }, {
    name: 'Pro',
    price: 29,
    currency: 'USD',
    billingType: 'recurring_both'
  }]
};
function OptionButton({
  selected,
  onClick,
  children,
  className = ''
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return <button onClick={onClick} className={`text-left border rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${selected ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-sm' : 'border-border hover:border-primary/50 hover:bg-muted/50'} ${className}`} data-id="element-1335">
      {children}
    </button>;
}
function ToggleSwitch({
  checked,
  onChange,
  label,
  description
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  label: string;
  description: string;
}) {
  return <label className="flex items-center justify-between p-4 border border-border rounded-xl cursor-pointer hover:bg-muted/30 transition-colors group" data-id="element-1336">
      <div className="flex-1 mr-4" data-id="element-1337">
        <div className="font-semibold text-base" data-id="element-1338">{label}</div>
        <div className="text-sm text-muted-foreground" data-id="element-1339">{description}</div>
      </div>
      <button role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${checked ? 'bg-primary' : 'bg-border'}`} data-id="element-1340">
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1341" />
      </button>
    </label>;
}
function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder: string;
}) {
  return <div data-id="element-1342">
      <label className="block text-sm font-medium mb-2" data-id="element-1343">{label}</label>
      <div className="relative" data-id="element-1344">
        <select value={value} onChange={e => onChange(e.target.value)} className="w-full appearance-none bg-background border border-border rounded-lg px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors cursor-pointer" data-id="element-1345">
          <option value="" data-id="element-1346">{placeholder}</option>
          {options.map(opt => <option key={opt} value={opt} data-id="element-1347">
              {opt}
            </option>)}
        </select>
        <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" data-id="element-1348" />
      </div>
    </div>;
}
function ExplainHelper({
  text
}: {
  text: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="mt-4" data-id="element-1349">
      <button onClick={() => setOpen(!open)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors" data-id="element-1350">
        <HelpCircleIcon className="w-4 h-4" data-id="element-1351" />
        {open ? 'Hide explanation' : 'Explain this'}
      </button>
      {open && <div className="mt-2 p-3 bg-muted/50 border border-border rounded-lg text-sm text-muted-foreground animate-slide-in" data-id="element-1352">
          {text}
        </div>}
    </div>;
}
export function SetupWizardPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const navigate = useNavigate();
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      localStorage.setItem('paykit_wizard_state', JSON.stringify(state));
      navigate('/setup/recommendation');
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  // Compute recommended stack summary
  const moduleCount = state.chargeModel ? state.chargeModel === 'Subscription' || state.chargeModel === 'Multiple' ? 4 : 3 : 0;
  const webhookCount = moduleCount > 0 ? moduleCount + 2 : 0;
  const getReviewValue = (stepId: string): string => {
    switch (stepId) {
      case 'type':
        return state.buildingType || 'Not selected';
      case 'charge':
        return state.chargeModel || 'Not selected';
      case 'products':
        return state.products.length > 0 ? `${state.products.length} products` : 'None defined';
      case 'features':
        {
          const active = Object.entries(state.features).filter(([, v]) => v).map(([k]) => k === 'trials' ? 'Free Trials' : k === 'coupons' ? 'Coupons' : k === 'tax' ? 'Tax' : 'Invoices');
          return active.length > 0 ? active.join(', ') : 'None selected';
        }
      case 'billing':
        return state.billingManager || 'Not selected';
      case 'stack':
        {
          const parts = [state.stack.frontend, state.stack.backend, state.stack.database].filter(Boolean);
          return parts.length > 0 ? parts.join(' + ') : 'Not selected';
        }
      case 'stripe':
        return state.stripeAccount ? state.stripeAccount === 'Yes' ? `Yes${state.stripeAccountId ? ` (${state.stripeAccountId})` : ''}` : state.stripeAccount : 'Not selected';
      default:
        return 'Not selected';
    }
  };
  return <div className="flex-1 flex flex-col md:flex-row h-[calc(100vh-64px)] overflow-hidden" data-id="element-1353">
      {/* Sidebar */}
      <div className="w-full md:w-80 border-r border-border bg-pk-surface p-6 overflow-y-auto hidden md:block" data-id="element-1354">
        <div className="mb-8" data-id="element-1355">
          <h3 className="font-semibold text-lg mb-6" data-id="element-1356">Setup Progress</h3>
          <StepIndicator steps={STEPS} currentStepIndex={currentStep} direction="vertical" data-id="element-1357" />
        </div>

        {/* Floating Summary Panel */}
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm" data-id="element-1358">
          <h4 className="text-sm font-semibold mb-3" data-id="element-1359">Recommended Stack</h4>
          <div className="space-y-2 text-sm" data-id="element-1360">
            <div className="flex justify-between items-center" data-id="element-1361">
              <span className="text-muted-foreground" data-id="element-1362">Type</span>
              <span className="font-medium" data-id="element-1363">{state.buildingType || '—'}</span>
            </div>
            <div className="flex justify-between items-center" data-id="element-1364">
              <span className="text-muted-foreground" data-id="element-1365">Model</span>
              <span className="font-medium" data-id="element-1366">{state.chargeModel || '—'}</span>
            </div>
            <div className="flex justify-between items-center" data-id="element-1367">
              <span className="text-muted-foreground" data-id="element-1368">Modules</span>
              <span className="font-medium" data-id="element-1369">
                {moduleCount > 0 ? moduleCount : '—'}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-1370">
              <span className="text-muted-foreground" data-id="element-1371">Webhooks</span>
              <span className="font-medium" data-id="element-1372">
                {webhookCount > 0 ? webhookCount : '—'}
              </span>
            </div>
            {state.stack.frontend && <div className="flex justify-between items-center" data-id="element-1373">
                <span className="text-muted-foreground" data-id="element-1374">Stack</span>
                <span className="font-medium text-right text-xs" data-id="element-1375">
                  {[state.stack.frontend, state.stack.backend].filter(Boolean).join(' + ')}
                </span>
              </div>}
          </div>
        </div>

        <button className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground text-center py-2 transition-colors" data-id="element-1376">
          Save and return later
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-y-auto bg-background relative" data-id="element-1377">
        {/* Mobile progress */}
        <div className="md:hidden p-4 border-b border-border bg-pk-surface" data-id="element-1378">
          <div className="text-sm font-medium text-muted-foreground mb-2" data-id="element-1379">
            Step {currentStep + 1} of {STEPS.length}
          </div>
          <div className="h-2 bg-border rounded-full overflow-hidden" data-id="element-1380">
            <div className="h-full bg-primary transition-all" style={{
            width: `${(currentStep + 1) / STEPS.length * 100}%`
          }} data-id="element-1381" />
          </div>
        </div>

        <div className="flex-1 p-6 md:p-12 max-w-3xl mx-auto w-full" data-id="element-1382">
          {/* Step 1: What are you building? */}
          {currentStep === 0 && <div className="animate-slide-in" data-id="element-1383">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1384">
                What are you building?
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-1385">
                This helps us recommend the right Stripe architecture for your
                product.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1386">
                {[{
              name: 'SaaS App',
              desc: 'Web-based software with user accounts'
            }, {
              name: 'Marketplace',
              desc: 'Platform connecting buyers and sellers'
            }, {
              name: 'Mobile App',
              desc: 'iOS or Android application'
            }, {
              name: 'API / Platform',
              desc: 'Developer-facing API or platform'
            }, {
              name: 'E-commerce',
              desc: 'Online store selling products'
            }, {
              name: 'Other',
              desc: 'Something else entirely'
            }].map(option => <OptionButton key={option.name} selected={state.buildingType === option.name} onClick={() => setState(s => ({
              ...s,
              buildingType: option.name
            }))} className="p-6" data-id="element-1387">
                    <h3 className="font-semibold text-lg" data-id="element-1388">{option.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-1389">
                      {option.desc}
                    </p>
                  </OptionButton>)}
              </div>

              <ExplainHelper text="Your product type determines which Stripe modules we recommend. For example, marketplaces need Stripe Connect, while SaaS apps typically use Checkout + Billing." data-id="element-1390" />
            </div>}

          {/* Step 2: How do you charge? */}
          {currentStep === 1 && <div className="animate-slide-in" data-id="element-1391">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1392">How do you charge?</h2>
              <p className="text-muted-foreground mb-8" data-id="element-1393">
                Select your primary revenue model.
              </p>

              <div className="space-y-4" data-id="element-1394">
                {[{
              title: 'Subscription',
              desc: 'Recurring payments — monthly, annually, or custom intervals.'
            }, {
              title: 'One-time',
              desc: 'Single payments for lifetime access or digital goods.'
            }, {
              title: 'Usage-based',
              desc: 'Charge based on metered usage (e.g., per API call, per seat).'
            }, {
              title: 'Freemium',
              desc: 'Free tier with paid upgrades or premium features.'
            }, {
              title: 'Multiple',
              desc: 'A mix of subscriptions, one-time, and usage-based charges.'
            }].map(option => <OptionButton key={option.title} selected={state.chargeModel === option.title} onClick={() => setState(s => ({
              ...s,
              chargeModel: option.title
            }))} className="w-full p-5 flex flex-col" data-id="element-1395">
                    <span className="font-semibold text-lg" data-id="element-1396">
                      {option.title}
                    </span>
                    <span className="text-muted-foreground text-sm mt-0.5" data-id="element-1397">
                      {option.desc}
                    </span>
                  </OptionButton>)}
              </div>

              <ExplainHelper text="Your revenue model determines how we configure Stripe products and prices. Subscriptions use Stripe Billing, one-time payments use Checkout sessions, and usage-based models need metered billing with usage records." data-id="element-1398" />
            </div>}

          {/* Step 3: Products & Pricing */}
          {currentStep === 2 && <div className="animate-slide-in" data-id="element-1399">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1400">Products & Pricing</h2>
              <p className="text-muted-foreground mb-8" data-id="element-1401">
                Define the products and prices you want to sell.
              </p>

              <div className="space-y-4" data-id="element-1402">
                {state.products.map((product, index) => <div key={index} className="p-4 border border-border rounded-xl bg-card flex flex-col gap-4" data-id="element-1403">
                    <div className="flex justify-between items-center" data-id="element-1404">
                      <h3 className="font-semibold" data-id="element-1405">Product {index + 1}</h3>
                      <button onClick={() => {
                  const newProducts = [...state.products];
                  newProducts.splice(index, 1);
                  setState(s => ({
                    ...s,
                    products: newProducts
                  }));
                }} className="text-destructive text-sm hover:underline" data-id="element-1406">
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1407">
                      <div data-id="element-1408">
                        <label className="block text-sm font-medium mb-1" data-id="element-1409">
                          Name
                        </label>
                        <input type="text" value={product.name} onChange={e => {
                    const newProducts = [...state.products];
                    newProducts[index].name = e.target.value;
                    setState(s => ({
                      ...s,
                      products: newProducts
                    }));
                  }} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. Pro Plan" data-id="element-1410" />
                      </div>
                      <div data-id="element-1411">
                        <label className="block text-sm font-medium mb-1" data-id="element-1412">
                          Price
                        </label>
                        <div className="flex gap-2" data-id="element-1413">
                          <select value={product.currency} onChange={e => {
                      const newProducts = [...state.products];
                      newProducts[index].currency = e.target.value;
                      setState(s => ({
                        ...s,
                        products: newProducts
                      }));
                    }} className="bg-background border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-20" data-id="element-1414">
                            <option value="USD" data-id="element-1415">USD</option>
                            <option value="GBP" data-id="element-1416">GBP</option>
                            <option value="EUR" data-id="element-1417">EUR</option>
                          </select>
                          <input type="number" value={product.price} onChange={e => {
                      const newProducts = [...state.products];
                      newProducts[index].price = parseFloat(e.target.value) || 0;
                      setState(s => ({
                        ...s,
                        products: newProducts
                      }));
                    }} className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="0.00" data-id="element-1418" />
                        </div>
                      </div>
                      <div className="col-span-2" data-id="element-1419">
                        <label className="block text-sm font-medium mb-1" data-id="element-1420">
                          Billing Type
                        </label>
                        <select value={product.billingType} onChange={e => {
                    const newProducts = [...state.products];
                    newProducts[index].billingType = e.target.value as any;
                    setState(s => ({
                      ...s,
                      products: newProducts
                    }));
                  }} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-1421">
                          <option value="recurring_monthly" data-id="element-1422">
                            Monthly Recurring
                          </option>
                          <option value="recurring_annual" data-id="element-1423">
                            Annual Recurring
                          </option>
                          <option value="recurring_both" data-id="element-1424">
                            Both Monthly & Annual
                          </option>
                          <option value="one_time" data-id="element-1425">One-time Payment</option>
                        </select>
                      </div>
                    </div>
                  </div>)}

                <button onClick={() => {
              setState(s => ({
                ...s,
                products: [...s.products, {
                  name: '',
                  price: 0,
                  currency: 'USD',
                  billingType: 'recurring_monthly'
                }]
              }));
            }} className="w-full py-3 border border-dashed border-border rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors flex items-center justify-center gap-2 text-sm font-medium" data-id="element-1426">
                  + Add Product
                </button>
              </div>

              <ExplainHelper text="We'll generate Stripe Product and Price objects based on these definitions. Recurring prices will be set up for Stripe Billing, while one-time prices will use standard Checkout." data-id="element-1427" />
            </div>}

          {/* Step 4: Features & Invoices */}
          {currentStep === 3 && <div className="animate-slide-in" data-id="element-1428">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1429">
                Trials, coupons, tax & invoices
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-1430">
                Toggle the features you need. We'll configure Stripe
                accordingly.
              </p>

              <div className="space-y-4" data-id="element-1431">
                <ToggleSwitch checked={state.features.trials} onChange={val => setState(s => ({
              ...s,
              features: {
                ...s.features,
                trials: val
              }
            }))} label="Free Trials" description="Let users try your product before paying. We'll configure trial periods on your subscription prices." data-id="element-1432" />
                <ToggleSwitch checked={state.features.coupons} onChange={val => setState(s => ({
              ...s,
              features: {
                ...s.features,
                coupons: val
              }
            }))} label="Coupon Codes" description="Offer promotional discounts. We'll set up Stripe coupon and promotion code objects." data-id="element-1433" />
                <ToggleSwitch checked={state.features.tax} onChange={val => setState(s => ({
              ...s,
              features: {
                ...s.features,
                tax: val
              }
            }))} label="Tax Collection" description="Automatically calculate and collect sales tax, VAT, or GST via Stripe Tax." data-id="element-1434" />
                <ToggleSwitch checked={state.features.invoices} onChange={val => setState(s => ({
              ...s,
              features: {
                ...s.features,
                invoices: val
              }
            }))} label="Invoice Generation" description="Generate and send PDF invoices to customers for each payment." data-id="element-1435" />
              </div>

              <ExplainHelper text="Each toggle adds specific Stripe configuration. Free trials add trial_period_days to prices. Coupons create promotion code objects. Tax enables Stripe Tax automatic calculation. Invoices configure invoice settings on the customer object." data-id="element-1436" />
            </div>}

          {/* Step 5: Billing Management */}
          {currentStep === 4 && <div className="animate-slide-in" data-id="element-1437">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1438">Who manages billing?</h2>
              <p className="text-muted-foreground mb-8" data-id="element-1439">
                This determines whether we set up a Customer Portal for
                self-service.
              </p>

              <div className="space-y-4" data-id="element-1440">
                {[{
              title: 'Customers self-serve',
              desc: 'Customers can update payment methods, switch plans, and cancel on their own via Stripe Customer Portal.'
            }, {
              title: 'Admin manages',
              desc: 'Your team handles all billing changes. Customers contact support for upgrades or cancellations.'
            }, {
              title: 'Both',
              desc: 'Customers can self-serve common tasks, but admins retain full control for complex changes.'
            }].map(option => <OptionButton key={option.title} selected={state.billingManager === option.title} onClick={() => setState(s => ({
              ...s,
              billingManager: option.title
            }))} className="w-full p-5 flex flex-col" data-id="element-1441">
                    <span className="font-semibold text-lg" data-id="element-1442">
                      {option.title}
                    </span>
                    <span className="text-muted-foreground text-sm mt-0.5" data-id="element-1443">
                      {option.desc}
                    </span>
                  </OptionButton>)}
              </div>

              <ExplainHelper text="If customers self-serve, we'll configure the Stripe Customer Portal with options for plan changes, cancellations, and payment method updates. Admin-only setups skip the portal configuration." data-id="element-1444" />
            </div>}

          {/* Step 6: Tech Stack */}
          {currentStep === 5 && <div className="animate-slide-in" data-id="element-1445">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1446">
                What is your tech stack?
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-1447">
                We'll tailor your implementation pack with code snippets and
                patterns for your specific stack.
              </p>

              <div className="space-y-6" data-id="element-1448">
                <SelectField label="Frontend Framework" value={state.stack.frontend} onChange={val => setState(s => ({
              ...s,
              stack: {
                ...s.stack,
                frontend: val
              }
            }))} options={['Next.js', 'React', 'Vue', 'Svelte', 'Angular', 'Other']} placeholder="Select your frontend..." data-id="element-1449" />
                <SelectField label="Backend / Server" value={state.stack.backend} onChange={val => setState(s => ({
              ...s,
              stack: {
                ...s.stack,
                backend: val
              }
            }))} options={['Node.js', 'Python', 'Ruby', 'Go', 'PHP', 'Java', 'Other']} placeholder="Select your backend..." data-id="element-1450" />
                <SelectField label="Database" value={state.stack.database} onChange={val => setState(s => ({
              ...s,
              stack: {
                ...s.stack,
                database: val
              }
            }))} options={['PostgreSQL', 'MySQL', 'MongoDB', 'Supabase', 'PlanetScale', 'Firebase', 'Other']} placeholder="Select your database..." data-id="element-1451" />
              </div>

              <ExplainHelper text="Your tech stack determines the language and framework of code snippets in your implementation pack. For example, Next.js gets API route handlers, while Python gets Flask/FastAPI endpoints." data-id="element-1452" />
            </div>}

          {/* Step 7: Stripe Account */}
          {currentStep === 6 && <div className="animate-slide-in" data-id="element-1453">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1454">
                Do you already have Stripe?
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-1455">
                We need to know if you have an existing Stripe account to
                connect.
              </p>

              <div className="space-y-4" data-id="element-1456">
                {[{
              title: 'Yes',
              desc: 'I have a Stripe account and want to connect it.'
            }, {
              title: 'No',
              desc: "I don't have one yet — help me set up."
            }, {
              title: 'Not sure',
              desc: "I'm not sure if my team already has one."
            }].map(option => <OptionButton key={option.title} selected={state.stripeAccount === option.title} onClick={() => setState(s => ({
              ...s,
              stripeAccount: option.title,
              stripeAccountId: option.title !== 'Yes' ? '' : s.stripeAccountId
            }))} className="w-full p-5 flex flex-col" data-id="element-1457">
                    <span className="font-semibold text-lg" data-id="element-1458">
                      {option.title}
                    </span>
                    <span className="text-muted-foreground text-sm mt-0.5" data-id="element-1459">
                      {option.desc}
                    </span>
                  </OptionButton>)}
              </div>

              {state.stripeAccount === 'Yes' && <div className="mt-6 p-5 border border-border rounded-xl bg-muted/30 animate-slide-in" data-id="element-1460">
                  <label className="block text-sm font-medium mb-2" data-id="element-1461">
                    Stripe Account ID (optional)
                  </label>
                  <input type="text" value={state.stripeAccountId} onChange={e => setState(s => ({
              ...s,
              stripeAccountId: e.target.value
            }))} placeholder="acct_1N2M3L4K..." className="w-full bg-background border border-border rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-muted-foreground/50" data-id="element-1462" />
                  <p className="text-xs text-muted-foreground mt-2" data-id="element-1463">
                    You can find this in your Stripe Dashboard under Settings →
                    Account details.
                  </p>
                </div>}

              {state.stripeAccount === 'No' && <div className="mt-6 p-5 border border-primary/20 rounded-xl bg-primary/5 animate-slide-in" data-id="element-1464">
                  <p className="text-sm" data-id="element-1465">
                    <span className="font-semibold" data-id="element-1466">No problem!</span> We'll
                    guide you through creating a Stripe account during the
                    provisioning step. You can continue the setup wizard now.
                  </p>
                </div>}

              <ExplainHelper text="If you have an existing Stripe account, we'll connect to it and configure everything there. If not, we'll help you create one. Either way, PayKit only needs limited permissions to set up products, prices, and webhooks." data-id="element-1467" />
            </div>}

          {/* Step 8: Review */}
          {currentStep === 7 && <div className="animate-slide-in" data-id="element-1468">
              <h2 className="text-3xl font-bold mb-2" data-id="element-1469">Review your answers</h2>
              <p className="text-muted-foreground mb-8" data-id="element-1470">
                Make sure everything looks correct before we generate your
                recommendation.
              </p>

              <div className="space-y-4" data-id="element-1471">
                {STEPS.slice(0, 7).map((step, i) => {
              const value = getReviewValue(step.id);
              const isNotSelected = value === 'Not selected' || value === 'None selected';
              return <div key={step.id} className="p-4 border border-border rounded-lg flex justify-between items-center bg-card hover:bg-muted/30 transition-colors" data-id="element-1472">
                      <div className="flex-1 mr-4" data-id="element-1473">
                        <div className="text-sm text-muted-foreground" data-id="element-1474">
                          {step.label}
                        </div>
                        <div className={`font-medium ${isNotSelected ? 'text-muted-foreground italic' : ''}`} data-id="element-1475">
                          {value}
                        </div>
                      </div>
                      <button onClick={() => setCurrentStep(i)} className="text-primary text-sm font-medium hover:underline shrink-0" data-id="element-1476">
                        Edit
                      </button>
                    </div>;
            })}
              </div>

              <div className="mt-8 p-4 bg-muted/30 border border-border rounded-lg text-sm text-muted-foreground" data-id="element-1477">
                Clicking{' '}
                <span className="font-semibold text-foreground" data-id="element-1478">
                  Get Recommendation
                </span>{' '}
                will generate a free, personalized Stripe architecture based on
                your answers.
              </div>
            </div>}
        </div>

        {/* Bottom Navigation */}
        <div className="border-t border-border bg-background p-6 sticky bottom-0 z-10" data-id="element-1479">
          <div className="max-w-3xl mx-auto flex items-center justify-between" data-id="element-1480">
            <button onClick={handleBack} disabled={currentStep === 0} className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${currentStep === 0 ? 'text-muted-foreground opacity-50 cursor-not-allowed' : 'text-foreground hover:bg-muted'}`} data-id="element-1481">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-1482" />
              Back
            </button>

            <div className="flex items-center gap-4" data-id="element-1483">
              <span className="text-sm text-muted-foreground hidden sm:block" data-id="element-1484">
                Step {currentStep + 1} of {STEPS.length}
              </span>
              <button onClick={handleNext} className="flex items-center gap-2 bg-primary text-primary-foreground hover:opacity-90 px-6 py-2.5 rounded-md font-medium transition-opacity" data-id="element-1485">
                {currentStep === STEPS.length - 1 ? 'Get Recommendation' : 'Continue'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1486" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}